import type { ReactNode } from 'react'
import { cx } from '@/app/lib/utils'
import styles from './ProgressCircleIndeterminate.module.scss'
import {
  ARC_FRACTION,
  DEFAULT_LABEL,
  DEFAULT_SIZE,
  SIZE_MAP,
  STROKE_WIDTH,
} from './constants'
import type { ProgressCircleIndeterminateProps } from './types'

/**
 * ProgressCircleIndeterminate — spinner circular de carga del DS Entradas.
 *
 * Server Component: es puramente presentacional, sin estado ni interacción,
 * y la animación se resuelve con CSS (`@keyframes`), por lo que no requiere
 * `'use client'` y se renderiza en el servidor.
 *
 * Accesibilidad (WCAG 2.1 AA):
 * - Es un loader de estado indeterminado (no hay valor que comunicar), así que
 *   se usa `role="status"` con un texto oculto visualmente como nombre accesible,
 *   en lugar de `role="progressbar"` (que esperaría valores).
 * - El SVG es decorativo (`aria-hidden`); el anuncio lo da el texto del status.
 * - La rotación se desactiva bajo `prefers-reduced-motion` (ver SCSS).
 *
 * La geometría del círculo (diámetro, radio) depende de la prop `size`, por lo
 * que se calcula y se aplica como atributos/inline del SVG; es la vía idiomática
 * del repo para dimensiones dirigidas por props (cf. ProgressBar/Skeleton).
 */
export function ProgressCircleIndeterminate({
  size = DEFAULT_SIZE,
  label = DEFAULT_LABEL,
  className,
}: ProgressCircleIndeterminateProps): ReactNode {
  const diameter = SIZE_MAP[size]
  const center = diameter / 2
  const radius = (diameter - STROKE_WIDTH) / 2
  const circumference = 2 * Math.PI * radius
  // Arco visible (~75% de la circunferencia) seguido del hueco restante.
  const dashArray = `${circumference * ARC_FRACTION} ${circumference * (1 - ARC_FRACTION)}`

  return (
    <span role="status" className={cx(styles.root, className)}>
      <svg
        className={styles.svg}
        width={diameter}
        height={diameter}
        viewBox={`0 0 ${diameter} ${diameter}`}
        fill="none"
        aria-hidden="true"
        focusable="false"
      >
        {/* Pista de fondo */}
        <circle
          className={styles.track}
          cx={center}
          cy={center}
          r={radius}
          strokeWidth={STROKE_WIDTH}
        />
        {/* Arco que gira (el giro y el offset inicial se aplican en CSS) */}
        <circle
          className={styles.arc}
          cx={center}
          cy={center}
          r={radius}
          strokeWidth={STROKE_WIDTH}
          strokeLinecap="round"
          strokeDasharray={dashArray}
        />
      </svg>
      <span className={styles.label}>{label}</span>
    </span>
  )
}
